import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Account-visibility store — per-browser allowlist of account ids
 * the user has chosen to hide from the selector dropdown.
 *
 * Why this exists
 * ---------------
 * The WHB backend returns **all** broker accounts the user's role can
 * see. For admin users (Sergey) that means other customers' accounts
 * appear in the list mixed with his own 4. There's no server-side
 * filter — every `?userId=…` / `?ownerId=…` / `?my=true` variant we
 * probed still returns the full 23-item list. Until the backend grows
 * a "scope to current user" endpoint (tracked as API follow-up from
 * #270), the PWA offers a client-side hide list so the user can
 * curate the selector themselves.
 *
 * Contract
 * --------
 * • `hiddenIds`   — set-semantics (no duplicates enforced in the
 *                   selectors). Stored as a string array for easy
 *                   JSON serialisation.
 * • `isHidden`    — convenience read helper.
 * • `hide/show`   — idempotent add/remove of a single id.
 * • `toggle`      — flip the hidden state of a single id.
 * • `reset`       — clear the entire list (makes every account
 *                   visible again).
 *
 * Lifetime
 * --------
 * Persists to localStorage under `whb-account-vis` so the choices
 * survive refresh and re-login. Wiped on `signOut` from the auth
 * store so a different user signing in on the same browser doesn't
 * inherit the previous user's hide list.
 */
interface AccountVisibilityState {
  hiddenIds: string[];
  isHidden: (id: string) => boolean;
  hide: (id: string) => void;
  show: (id: string) => void;
  toggle: (id: string) => void;
  reset: () => void;
}

export const useAccountVisibilityStore = create<AccountVisibilityState>()(
  persist(
    (set, get) => ({
      hiddenIds: [],
      isHidden: (id) => get().hiddenIds.includes(id),
      hide: (id) =>
        set((s) =>
          s.hiddenIds.includes(id)
            ? s
            : { hiddenIds: [...s.hiddenIds, id] }
        ),
      show: (id) =>
        set((s) => ({ hiddenIds: s.hiddenIds.filter((x) => x !== id) })),
      toggle: (id) =>
        set((s) =>
          s.hiddenIds.includes(id)
            ? { hiddenIds: s.hiddenIds.filter((x) => x !== id) }
            : { hiddenIds: [...s.hiddenIds, id] }
        ),
      reset: () => set({ hiddenIds: [] }),
    }),
    {
      name: "whb-account-vis",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
